import React, { useState } from "react";
import { Card, Table, Badge, Button, Alert } from "react-bootstrap";
import useSWR, { mutate } from "swr";
import { serverUrl, fetcher, swrConfig } from "../../../../helpers/utility";
import Loading from "../../../../components/loading";
import { format, isAfter } from "date-fns";
import axios from "axios";

function InvitesList({ user }) {
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const { data, error: fetchError, isLoading } = useSWR(
    `${serverUrl}/local-authority/invites`,
    fetcher,
    swrConfig
  );

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm("Are you sure you want to cancel this invite?")) {
      return;
    }

    setDeletingId(inviteId);
    setError(null);

    try {
      const response = await axios.delete(`${serverUrl}/invite/${inviteId}`);
      if (response.data?.success) {
        mutate(`${serverUrl}/local-authority/invites`);
      } else {
        setError(response.data?.message || "Failed to cancel invite");
      }
    } catch (err) {
      console.error("Error cancelling invite:", err);
      setError(err.response?.data?.message || "Failed to cancel invite");
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (fetchError) {
    return (
      <Card>
        <Card.Body>
          <p className="text-danger">Error loading invites. Please try again.</p>
        </Card.Body>
      </Card>
    );
  }

  const invites = data?.invites || [];

  const getStatusBadge = (invite) => {
    if (invite.status === "ACCEPTED") {
      return <Badge bg="success">Accepted</Badge>;
    }
    if (invite.status === "EXPIRED" || invite.status === "CANCELLED") {
      return <Badge bg="secondary">{invite.status}</Badge>;
    }
    // PENDING
    if (invite.expiresAt && isAfter(new Date(), new Date(invite.expiresAt))) {
      return <Badge bg="warning">Expired</Badge>;
    }
    return <Badge bg="info">Pending</Badge>;
  };

  const pendingInvites = invites.filter((inv) => inv.status === "PENDING");
  const otherInvites = invites.filter((inv) => inv.status !== "PENDING");

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          Invites ({pendingInvites.length} pending, {otherInvites.length} other)
        </h5>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {invites.length === 0 ? (
          <p className="text-muted">No invites sent yet.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite._id}>
                  <td>{invite.email}</td>
                  <td>
                    <Badge bg="secondary">{invite.role || "admin"}</Badge>
                  </td>
                  <td>{getStatusBadge(invite)}</td>
                  <td>
                    {invite.created
                      ? format(new Date(invite.created), "dd/MM/yyyy HH:mm")
                      : "—"}
                  </td>
                  <td>
                    {invite.expiresAt
                      ? format(new Date(invite.expiresAt), "dd/MM/yyyy HH:mm")
                      : "—"}
                  </td>
                  <td>
                    {invite.status === "PENDING" && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleCancelInvite(invite._id)}
                        disabled={deletingId === invite._id}
                      >
                        {deletingId === invite._id ? "Cancelling..." : "Cancel"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default InvitesList;

